import axios from 'axios';
import {API_URL, MEDIA_TYPE_V2} from '../config/constants';
import SpringDataPage, {BasicPage} from '../@types/spring-data-page';
import {getErrorMessage} from '../utils/utils';
import PsfTemplate, {PsfTemplateEventSequenceLink, PsfTemplateLinks} from '../@types/psfTemplate';
import {makePsfTemplate} from '../utils/psf-template-utils';
import {makeGroup} from '../utils/group-utils';


class PsfTemplateService {
    static psfTemplateUrlV2 = API_URL + 'api/v2/influences/psf/templates';

    formPsfTemplate(psfTemplate: any): PsfTemplate {
        return makePsfTemplate(psfTemplate);
    }

    formPsfTemplatesPagePromise(psfTemplatesData: any): Promise<SpringDataPage<PsfTemplate>> {
        let psfTemplatesPage: SpringDataPage<PsfTemplate> = {} as SpringDataPage<PsfTemplate>;
        if (psfTemplatesData) {
            if (psfTemplatesData._embedded?.psfTemplateDtoList) {
                psfTemplatesPage.content = psfTemplatesData._embedded.psfTemplateDtoList?.map((psfTemplate: any) => this.formPsfTemplate(psfTemplate));
            } else {
                psfTemplatesPage.content = [];
            }
            psfTemplatesPage.page = psfTemplatesData.page;
        }
        return Promise.resolve(psfTemplatesPage);
    }

    formPsfTemplateEventSequenceLinks(linksData: any): PsfTemplateEventSequenceLink[] {
        const eventSequencePsfLinks: PsfTemplateEventSequenceLink[] = [];
        if (linksData && linksData.length > 0) {
            linksData.forEach((linkData: any) => {
                let link = {} as PsfTemplateEventSequenceLink;
                link.psfTemplateId = linkData.psfTemplateId;
                link.psfTemplateName = linkData.psfTemplateName;
                link.eventSequenceId = linkData.eventSequenceId;
                link.group = makeGroup(linkData.group);
                eventSequencePsfLinks.push(link);
            })
        }
        return eventSequencePsfLinks;
    }

    fetchPsfTemplates(page: BasicPage, groupId?: number|null, searchTerm?: string): Promise<SpringDataPage<PsfTemplate>> {
        let url = `${PsfTemplateService.psfTemplateUrlV2}?`
            + `size=${page.size}&`
            + `page=${page.offset}&`
            + `sort=${page.sort},${page.direction}`;
        if (searchTerm && searchTerm !== '') url += `&searchTerm=${searchTerm}`;
        if (groupId) url += `&groupId=${groupId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return this.formPsfTemplatesPagePromise(response.data);
            })
            .catch(err => {
                throw new Error('Failed to get psfTemplates: ' + getErrorMessage(err));
            })
    }

    fetchPsfTemplateById(psfTemplateId: number): Promise<PsfTemplate> {
        let url = `${PsfTemplateService.psfTemplateUrlV2}/${psfTemplateId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return Promise.resolve(this.formPsfTemplate(response.data));
            })
            .catch(err => {
                throw new Error(`Failed to get psfTemplate ${psfTemplateId} ` + getErrorMessage(err));
            })
    }

    createPsfTemplate(psfTemplate: PsfTemplate): Promise<any> {
        return axios
            .post(`${PsfTemplateService.psfTemplateUrlV2}`, psfTemplate, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(response => {
                return this.formPsfTemplate(response.data);
            })
            .catch(err => {
                throw new Error('Create psfTemplate failed with error: ' + getErrorMessage(err));
            })
    }

    putPsfTemplate(psfTemplate: PsfTemplate): Promise<any> {
        return axios
            .put(`${PsfTemplateService.psfTemplateUrlV2}/${psfTemplate.id}`, psfTemplate, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(response => {
                return this.formPsfTemplate(response.data);
            })
            .catch(err => {
                throw new Error('Create psfTemplate failed with error: ' + getErrorMessage(err));
            })
    }

    deletePsfTemplateById(id: number): Promise<void> {
        return axios
            .delete(PsfTemplateService.psfTemplateUrlV2 + '/' + id, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(() => {
                return;
            })
            .catch(err => {
                throw new Error(`Delete psfTemplate ${id} failed with error: ` + getErrorMessage(err));
            })
    }

    duplicatePsfTemplate(sourceResourceId: number | string, targetGroupId: number | string | undefined): Promise<any> {
        const data = targetGroupId !== undefined ? {
            sourceResourceId: sourceResourceId,
            targetGroupId: targetGroupId
        } : {
            sourceResourceId: sourceResourceId
        };

        return axios.post(PsfTemplateService.psfTemplateUrlV2 + '/copy-and-paste', data , {
            withCredentials: true,
            headers: { 'Content-Type': MEDIA_TYPE_V2 }
        }).then((response: any) => {
            return response;
        }).catch((error: any) => {
            throw new Error(`Duplicate psfTemplate ${sourceResourceId} failed with error: ` + getErrorMessage(error));
        });
    }

    fetchLinksByPsfTemplateId(psfTemplateId: number|string): Promise<PsfTemplateLinks> {
        let url = `${PsfTemplateService.psfTemplateUrlV2}/${psfTemplateId}/links`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                let psfTemplateLinks = {} as PsfTemplateLinks;
                psfTemplateLinks.psfTemplateId = response.data.psfTemplateId;
                psfTemplateLinks.eventSequenceIds = response.data.eventSequenceIds;
                return Promise.resolve(psfTemplateLinks);
            })
            .catch(err => {
                throw new Error(`Failed to get links for psfTemplate ${psfTemplateId}: ` + getErrorMessage(err));
            })
    }

    fetchLinksByGroupId(groupId: number|string): Promise<PsfTemplateEventSequenceLink[]> {
        let url = `${PsfTemplateService.psfTemplateUrlV2}/groups/${groupId}/links`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return response.data._embedded ? this.formPsfTemplateEventSequenceLinks(response.data._embedded.psfTemplateEventSequenceDtoList) : [];
            })
            .catch(err => {
                throw new Error(`Failed to get links for Group ${groupId}: ` + getErrorMessage(err));
            })
    }

    savePsfEventSequenceLinks(psfTemplateLinks: PsfTemplateLinks): Promise<void> {
        return axios
            .post(`${PsfTemplateService.psfTemplateUrlV2}/${psfTemplateLinks.psfTemplateId}/link`, psfTemplateLinks, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(() => { return; })
            .catch(err => {
                throw new Error('Create psfTemplate failed with error: ' + getErrorMessage(err));
            })
    }
}

export default new PsfTemplateService();